import { useState } from 'react';
import { ASRProvider, RecordingState } from '~/types';

interface RecordingControlsProps {
  state: RecordingState;
  onStart: (provider: ASRProvider, title?: string) => Promise<void>;
  onPause: () => Promise<void>;
  onResume: () => Promise<void>;
  onStop: () => Promise<void>;
}

export function RecordingControls({
  state,
  onStart,
  onPause,
  onResume,
  onStop,
}: RecordingControlsProps) {
  const [provider, setProvider] = useState<ASRProvider>('baidu');
  const [title, setTitle] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleStart = async () => {
    setIsLoading(true);
    try {
      await onStart(provider, title || undefined);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePause = async () => {
    setIsLoading(true);
    try {
      await onPause();
    } finally {
      setIsLoading(false);
    }
  };

  const handleResume = async () => {
    setIsLoading(true);
    try {
      await onResume();
    } finally {
      setIsLoading(false);
    }
  };

  const handleStop = async () => {
    setIsLoading(true);
    try {
      await onStop();
    } finally {
      setIsLoading(false);
      setTitle('');
    }
  };

  const formatDuration = (ms: number): string => {
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes
      .toString()
      .padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
    <div style={{ padding: '20px', borderBottom: '1px solid #ddd' }}>
      <h2>录音控制</h2>

      {!state.isRecording && (
        <div style={{ marginBottom: '20px' }}>
          <div style={{ marginBottom: '10px' }}>
            <label>
              ASR Provider:{' '}
              <select
                value={provider}
                onChange={(e) => setProvider(e.target.value as ASRProvider)}
                disabled={isLoading}
              >
                <option value="baidu">Baidu</option>
                <option value="doubao">Doubao</option>
                <option value="tongyi">Tongyi</option>
              </select>
            </label>
          </div>

          <div style={{ marginBottom: '10px' }}>
            <label>
              标题（可选）:{' '}
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="会议录音"
                disabled={isLoading}
                style={{ width: '300px' }}
              />
            </label>
          </div>

          <button
            onClick={handleStart}
            disabled={isLoading}
            style={{
              padding: '10px 20px',
              fontSize: '16px',
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
          >
            {isLoading ? '启动中...' : '开始录音'}
          </button>
        </div>
      )}

      {state.isRecording && (
        <div>
          <div style={{ marginBottom: '10px' }}>
            <strong>状态:</strong>{' '}
            {state.isPaused ? '已暂停' : '录音中'}
          </div>
          <div style={{ marginBottom: '10px' }}>
            <strong>时长:</strong> {formatDuration(state.duration)}
          </div>
          <div style={{ marginBottom: '10px' }}>
            <strong>Provider:</strong> {state.provider}
          </div>
          <div style={{ marginBottom: '10px' }}>
            <strong>Session ID:</strong>{' '}
            <code style={{ fontSize: '12px' }}>{state.sessionId}</code>
          </div>

          <div style={{ display: 'flex', gap: '10px' }}>
            {!state.isPaused && (
              <button
                onClick={handlePause}
                disabled={isLoading}
                style={{
                  padding: '10px 20px',
                  fontSize: '16px',
                  backgroundColor: '#ffc107',
                  color: 'black',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                }}
              >
                {isLoading ? '暂停中...' : '暂停'}
              </button>
            )}

            {state.isPaused && (
              <button
                onClick={handleResume}
                disabled={isLoading}
                style={{
                  padding: '10px 20px',
                  fontSize: '16px',
                  backgroundColor: '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                }}
              >
                {isLoading ? '继续中...' : '继续'}
              </button>
            )}

            <button
              onClick={handleStop}
              disabled={isLoading}
              style={{
                padding: '10px 20px',
                fontSize: '16px',
                backgroundColor: '#dc3545',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
              }}
            >
              {isLoading ? '停止中...' : '结束并总结'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
